import React, { useEffect } from 'react';
import { Card, Typography, Space, Tag, Tooltip, Flex, theme } from 'antd';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import AudioMessage from './AudioMessage/AudioMessage';
import { Grid } from 'antd';
import VideoPlayer from './VideoPlayer/VideoPlayer';
import { useTheme } from '../../../../../Shared/GlobalContext/ThemeContext';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const renderTextWithLinks = (text, isAgentOrSupport, isDarkMode) => {
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlPattern);
    const linkColor = isDarkMode ? '#FFD700' : '#1890ff';

    return parts.map((part, index) => {
        if (part.match(urlPattern)) {
            return (
                <Text
                    key={index}
                    style={{
                        color: isAgentOrSupport ? linkColor : '#1890ff',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: '13px'
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        window.open(part, '_blank');
                    }}
                >
                    {part}
                </Text>
            );
        }
        return (
            <Text
                key={index}
                style={{
                    color: (isAgentOrSupport && isDarkMode) ? '#fff' : (isAgentOrSupport && !isDarkMode) ? 'inherit' : 'inherit',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '13px'
                }}
            >
                {part}
            </Text>
        );
    });
};

const ReplyPreview = ({ reply_to_message, isCustomerMessage, onClick, isDarkMode }) => {
    const { t } = useTranslation();
    if (!reply_to_message) return null;
    const borderColor = isDarkMode
        ? isCustomerMessage
            ? 'rgba(255, 255, 255, 0.8)'
            : '#FFD700'
        : isCustomerMessage
            ? 'rgba(255, 255, 255, 0.8)'
            : '#1890ff';
    const titleColor = isDarkMode
        ? isCustomerMessage
            ? 'rgba(255, 255, 255, 0.85)'
            : '#FFD700'
        : isCustomerMessage
            ? 'rgba(255, 255, 255, 0.85)'
            : '#1890ff';
    const contentColor = isDarkMode
        ? isCustomerMessage
            ? 'rgba(255, 255, 255, 0.75)'
            : 'rgba(255, 255, 255, 0.65)'
        : isCustomerMessage
            ? 'rgba(255, 255, 255, 0.75)'
            : 'rgba(0, 0, 0, 0.45)';
    return (
        <Space
            direction="vertical"
            size={0}
            style={{
                borderInlineStart: `2px solid ${borderColor}`,
                paddingInlineStart: 4,
                marginBottom: 4,
                cursor: 'pointer',
                maxWidth: '100%',
                overflow: 'hidden'
            }}
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
        >
            <Text strong style={{
                fontSize: '11px',
                color: titleColor
            }}>
                {reply_to_message.sender_type}
            </Text>
            <Text ellipsis style={{
                fontSize: '11px',
                color: contentColor
            }}>
                {reply_to_message.content || t('media_message')}
            </Text>
        </Space>
    );
};

const MessageStatus = ({ status, isCustomerMessage, isDarkMode }) => {
    const { t } = useTranslation();
    const statusColor = isDarkMode && isCustomerMessage ? 'rgba(255, 255, 255, 0.8)' : undefined;
    return (
        <Space size={1}>
            {status.delivered && (
                <Tooltip title={`${t('delivered_at')} ${format(parseISO(status.delivered_at), 'HH:mm')}`}>
                    <Text style={{ color: statusColor, fontSize: '10px' }}>✓</Text>
                </Tooltip>
            )}
            {status.read && (
                <Tooltip title={`${t('read_at')} ${format(parseISO(status.read_at), 'HH:mm')}`}>
                    <Text style={{ color: statusColor, fontSize: '10px' }}>✓✓</Text>
                </Tooltip>
            )}
        </Space>
    );
};

const MessageBubble = ({ message, onReplyClick, messageRefs, onMessageSelect }) => {
    const isAgentOrSupport = message.sender_type === 'agent' || message.sender_type === 'support';
    const screens = useBreakpoint();
    const { token } = theme.useToken();
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();

    const isFacebookPolicyError = message.error_type === 'sending_error' && 
        message.error_details && message.error_details.includes('Facebook business policy');
    
    useEffect(() => {
        if (messageRefs && message.id) {
            messageRefs.current[message.id] = React.createRef();
        }
    }, [message.id, messageRefs]);

    const handleReplyClick = () => {
        if (message.reply_to_message && onReplyClick) {
            onReplyClick(message.reply_to_message);
        }
    };

    const containerStyle = {
        display: 'flex',
        justifyContent: 'flex-start',
        width: '100%',
        marginBottom: 8,
    };

    const bubbleStyle = {
        maxWidth: screens.xs ? '90%' : '75%',
        width: message.medias?.length > 0 ? '100%' : 'auto',
        marginInlineStart: isAgentOrSupport ? 'auto' : '0',
        marginInlineEnd: isAgentOrSupport ? '0' : 'auto',
    };
    const messageBubbleBackground = isDarkMode
        ? isAgentOrSupport
            ? token.colorPrimary
            : '#2a2a2a'
        : isAgentOrSupport
            ? token.colorPrimary
            : '#fff';

    const messageBubbleTextColor = isDarkMode
        ? isAgentOrSupport
            ? '#fff'
            : '#fff'
        : isAgentOrSupport
            ? '#fff'
            : 'inherit';

    const messageAbuseTagColor = isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.45)'
    const messageAiTextColor = isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.65)';
    const messageAiDescriptionTextColor = isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.3)'

    return (
        <div style={containerStyle}>
            <div
                ref={messageRefs?.current[message.id]}
                style={bubbleStyle}
                onClick={() => onMessageSelect(message)}
            >
                <Card
                    size="small"
                    style={{
                        background: messageBubbleBackground,
                        color: messageBubbleTextColor,
                        width: '100%',
                        cursor: 'pointer',
                        opacity: isFacebookPolicyError ? 0.7 : 1,
                    }}
                    styles={{ body: { padding: '5px 12px' } }}
                >
                    {message.is_abuse && (
                        <Tag
                            color="red"
                            style={{
                                position: 'absolute',
                                bottom: -15,
                                insetInlineStart: 8,
                                fontWeight: 'bold',
                                borderRadius: 4,
                                border: 'none',
                                fontSize: '10px'
                            }}
                        >
                            ⚠️ {t('abusive_content')}
                        </Tag>
                    )}
                    
                    {isFacebookPolicyError && (
                        <Tag
                            color="orange"
                            style={{
                                position: 'absolute',
                                bottom: -15,
                                insetInlineStart: 8,
                                fontWeight: 'bold',
                                borderRadius: 4,
                                border: 'none',
                                fontSize: '10px'
                            }}
                        >
                            ⚠️ {t('facebook_policy_violation')}
                        </Tag>
                    )}
                    
                    {message.reply_to_message && (
                        <ReplyPreview
                            reply_to_message={message.reply_to_message}
                            isCustomerMessage={isAgentOrSupport}
                            onClick={handleReplyClick}
                            isDarkMode={isDarkMode}
                        />
                    )}

                    <Flex vertical size={4} style={{ width: '100%' }}>
                        {message.content && (
                            <div>
                                {renderTextWithLinks(message.content, isAgentOrSupport, isDarkMode)}
                            </div>
                        )}

                        {isFacebookPolicyError && (
                            <div style={{
                                marginTop: 4,
                                padding: 6,
                                background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 152, 0, 0.1)',
                                borderRadius: 4,
                                borderLeft: '3px solid orange'
                            }}>
                                <Text
                                    style={{
                                        display: 'block',
                                        fontSize: '11px',
                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.65)',
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {message.error_details}
                                </Text>
                                <Tooltip title={message.error_details}>
                                    <Text
                                        underline
                                        style={{
                                            fontSize: '10px',
                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.45)',
                                            cursor: 'help'
                                        }}
                                    >
                                        {t('learn_more')}
                                    </Text>
                                </Tooltip>
                            </div>
                        )}

                        {message.medias?.map((media, index) => (
                            <div key={index}>
                                {media.media_type === 'audio' && (
                                    <AudioMessage
                                        url={media.url}
                                        duration={media.data?.duration}
                                        isCustomerMessage={isAgentOrSupport}
                                        sender={message.sender_type}
                                    />
                                )}
                                {media.media_type === 'video' && (
                                    <VideoPlayer
                                        url={media.url}
                                        duration={media.data?.duration}
                                        isCustomerMessage={isAgentOrSupport}
                                    />
                                )}
                                {media.media_type === 'image' && (
                                    <div style={{
                                        background: isDarkMode ? '#f0f0f0' : '#ffffff',
                                        borderRadius: 4,
                                        padding: 4,
                                    }}>
                                        <img
                                            src={media.url}
                                            alt={t('message_attachment')}
                                            style={{
                                                maxWidth: '100%',
                                                borderRadius: 4,
                                                display: 'block'
                                            }}
                                        />
                                        {media.data && media.data.content_description && (
                                            <div style={{
                                                marginTop: 4,
                                                padding: 8,
                                                background: isAgentOrSupport ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                                                borderRadius: 4,
                                                position: 'relative'
                                            }}>
                                                <Text
                                                    style={{
                                                        display: 'block',
                                                        fontSize: '10px',
                                                        color: messageAiTextColor,
                                                        lineHeight: 1.4,
                                                        fontStyle: 'italic'
                                                    }}
                                                >
                                                    <span style={{ fontWeight: 500 }}>{t('ai_image_analysis')}:</span> {media.data.content_description}
                                                </Text>
                                                <Tooltip
                                                    title={t('ai_description_tooltip')}
                                                    placement="topRight"
                                                >
                                                    <span style={{
                                                        position: 'absolute',
                                                        insetInlineEnd: 4,
                                                        top: 4,
                                                        fontSize: '9px',
                                                        cursor: 'help',
                                                        color: messageAiDescriptionTextColor
                                                    }}>
                                                        🤖
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {(media.media_type === 'document' || media.media_type === 'pdf') && (
                                    <div style={{
                                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                                        borderRadius: 4,
                                        padding: 8,
                                        background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                                    }}>
                                        <Flex align="center" gap={8}>
                                            <div style={{ fontSize: '24px' }}>📄</div>
                                            <Flex vertical>
                                                <Text style={{
                                                    fontSize: '13px',
                                                    color: isDarkMode ? '#fff' : 'inherit',
                                                    fontWeight: 500
                                                }}>
                                                    {media.filename || t('document')}
                                                </Text>
                                                <Text
                                                    style={{
                                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.45)',
                                                        fontSize: '11px'
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(media.url, '_blank');
                                                    }}
                                                    underline
                                                >
                                                    {t('view_document')}
                                                </Text>
                                            </Flex>
                                        </Flex>
                                    </div>
                                )}
                            </div>
                        ))}

                        {!message.content && (!message.medias || message.medias?.length === 0) && isAgentOrSupport && (
                            <div>
                                <Text style={{
                                    color: messageAbuseTagColor,
                                    fontSize: '10px'
                                }}>
                                    {t('agent_declined_to_respond')}
                                </Text>
                            </div>
                        )}

                        <Space size={1} style={{ fontSize: '10px' }}>
                            <Text style={{
                                color: messageAbuseTagColor,
                                fontSize: '10px'
                            }}>
                                {format(parseISO(message.sent_at), 'HH:mm')}
                            </Text>
                            {!isFacebookPolicyError && (
                                <MessageStatus
                                    status={message.status}
                                    isCustomerMessage={isAgentOrSupport}
                                    isDarkMode={isDarkMode}
                                />
                            )}
                            {isFacebookPolicyError && (
                                <Tooltip title={message.error_details}>
                                    <Text style={{ 
                                        color: isDarkMode ? 'rgba(255, 200, 0, 0.8)' : 'orange', 
                                        fontSize: '10px',
                                        marginLeft: 4
                                    }}>
                                        ⚠️
                                    </Text>
                                </Tooltip>
                            )}
                        </Space>
                    </Flex>
                </Card>

                {message.reactions?.length > 0 && (
                    <Space size={2} style={{ marginTop: 2 }}>
                        {message.reactions.map((reaction, index) => (
                            <Tag key={index} style={{ padding: '0 4px', fontSize: '11px' }}>
                                {reaction.emoji}
                            </Tag>
                        ))}
                    </Space>
                )}
            </div>
        </div >
    );
};

export default MessageBubble;
